import { useQuery } from '@tanstack/react-query'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import api from 'services/api.service'
import {
  RuntimeWhitelabelResponse,
  getCurrentWhitelabelConfig,
  getWhitelabelTag,
  hydrateRuntimeWhitelabel,
} from 'whitelabel'

type WhitelabelConfigContextValue = {
  config: ReturnType<typeof getCurrentWhitelabelConfig>
  isLoading: boolean
}

const WhitelabelConfigContext = createContext<WhitelabelConfigContextValue>({
  config: getCurrentWhitelabelConfig(),
  isLoading: false,
})

const getRuntimeWhitelabelConfig = async (tag: string) => {
  return api
    .get<RuntimeWhitelabelResponse>(`/public/whitelabels/${tag}/config`)
    .then((response) => response.data)
}

export const useWhitelabelConfig = () => useContext(WhitelabelConfigContext)

export function WhitelabelConfigProvider({ children }: { children: React.ReactNode }) {
  const tag = getWhitelabelTag()
  const [configVersion, setConfigVersion] = useState(0)

  const { data, isLoading } = useQuery({
    queryKey: ['whitelabel-runtime-config', tag],
    queryFn: () => getRuntimeWhitelabelConfig(tag),
    enabled: !!tag,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 0,
  })

  useEffect(() => {
    if (!data) {
      return
    }

    hydrateRuntimeWhitelabel(data)
    setConfigVersion((currentVersion) => currentVersion + 1)
  }, [data])

  const value = useMemo(
    () => ({
      config: getCurrentWhitelabelConfig(),
      isLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- configVersion is an intentional cache-buster to re-read hydrated config
    [configVersion, isLoading],
  )

  return (
    <WhitelabelConfigContext.Provider value={value}>{children}</WhitelabelConfigContext.Provider>
  )
}
